import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from './mutation.js';
import { api, type AppelApi } from './client.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';

export interface EvenementAgenda {
  id: string;
  source: string;
  sorte: 'vernissage' | 'exposition' | 'foire' | 'rendez-vous' | 'echeance' | 'evenement';
  titre: string;
  debut: string;
  fin: string | null;
  journeeEntiere: boolean;
  lieu: string | null;
  lien: {
    type:
      'exposition' | 'rendez-vous' | 'document' | 'offre' | 'mouvement' | 'contrat' | 'evenement';
    id: string;
  };
}
export interface RendezVousPresente {
  id: string;
  titre: string;
  debut: string;
  fin: string;
  journeeEntiere: boolean;
  lieu: string;
  contactId: string | null;
  oeuvreIds: string[];
  expositionId: string | null;
  participants: string[];
  note: string;
  rappel: boolean;
  annuleLe: string | null;
}
export type CorpsRendezVous = Omit<RendezVousPresente, 'id' | 'annuleLe'>;
export interface EtatIcal {
  actif: boolean;
  creeLe: string | null;
  dernierAccesLe: string | null;
}
export interface PeriodeAgenda {
  du: string;
  au: string;
  sources?: readonly string[];
}
const chemin = (g: string) => `/g/${encodeURIComponent(g)}/agenda`;
export const agendaApi = {
  lire: (g: string, periode: PeriodeAgenda, appel?: AppelApi) => {
    const q = new URLSearchParams({ du: periode.du, au: periode.au });
    if (periode.sources) q.set('sources', periode.sources.join(','));
    return api.get<{ evenements: EvenementAgenda[]; sources: string[] }>(`${chemin(g)}?${q}`, {
      galerie: g,
      ...appel,
    });
  },
  rendezVous: (g: string, id: string, appel?: AppelApi) =>
    api.get<{ rendezVous: RendezVousPresente }>(
      `${chemin(g)}/rendez-vous/${encodeURIComponent(id)}`,
      { galerie: g, ...appel },
    ),
  creerRendezVous: (g: string, corps: CorpsRendezVous, appel?: AppelApi) =>
    api.post<{ rendezVous: RendezVousPresente }>(`${chemin(g)}/rendez-vous`, corps, {
      galerie: g,
      ...appel,
    }),
  modifierRendezVous: (g: string, id: string, corps: Partial<CorpsRendezVous>, appel?: AppelApi) =>
    api.patch<{ rendezVous: RendezVousPresente }>(
      `${chemin(g)}/rendez-vous/${encodeURIComponent(id)}`,
      corps,
      { galerie: g, ...appel },
    ),
  annulerRendezVous: (g: string, id: string, appel?: AppelApi) =>
    api.post<{ rendezVous: RendezVousPresente }>(
      `${chemin(g)}/rendez-vous/${encodeURIComponent(id)}/annuler`,
      {},
      { galerie: g, ...appel },
    ),
  etatIcal: (g: string, appel?: AppelApi) =>
    api.get<EtatIcal>(`${chemin(g)}/ical`, { galerie: g, ...appel }),
  creerIcal: (g: string, appel?: AppelApi) =>
    api.post<{ url: string }>(`${chemin(g)}/ical`, {}, { galerie: g, ...appel }),
  revoquerIcal: (g: string, appel?: AppelApi) =>
    api.delete(`${chemin(g)}/ical`, { galerie: g, ...appel }),
};
export function useAgenda(g: string, periode: PeriodeAgenda) {
  return useQuery({
    queryKey: cleMetier(g, 'agenda', periode),
    queryFn: (c) => agendaApi.lire(g, periode, appelDeRequete(c)),
  });
}
export function useRendezVous(g: string, id?: string) {
  return useQuery({
    queryKey: cleMetier(g, 'agenda', 'rendez-vous', id),
    queryFn: (c) => agendaApi.rendezVous(g, id!, appelDeRequete(c)),
    enabled: Boolean(id),
  });
}
export function useEtatIcal(g: string) {
  return useQuery({
    queryKey: cleMetier(g, 'agenda', 'ical'),
    queryFn: (c) => agendaApi.etatIcal(g, appelDeRequete(c)),
  });
}
function useMutationAgenda<T, R>(g: string, mutationFn: (corps: T, appel: AppelApi) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn,
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: cleMetier(g, 'agenda') });
      },
    }),
  );
}
export const useCreerRendezVous = (g: string) =>
  useMutationAgenda(g, (corps: CorpsRendezVous, a) => agendaApi.creerRendezVous(g, corps, a));
export const useModifierRendezVous = (g: string) =>
  useMutationAgenda(g, (v: { id: string; corps: Partial<CorpsRendezVous> }, a) =>
    agendaApi.modifierRendezVous(g, v.id, v.corps, a),
  );
export const useAnnulerRendezVous = (g: string) =>
  useMutationAgenda(g, (id: string, a) => agendaApi.annulerRendezVous(g, id, a));
// Le secret de création ne reste pas dans le cache de mutations après démontage.
export const useCreerIcal = (g: string) => {
  const qc = useQueryClient();
  return useMutation(
    dansLaPortee({
      gcTime: 0,
      mutationFn: (_: void, a) => agendaApi.creerIcal(g, a),
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: cleMetier(g, 'agenda', 'ical') });
      },
    }),
  );
};
export const useRevoquerIcal = (g: string) =>
  useMutationAgenda(g, (_: void, a) => agendaApi.revoquerIcal(g, a));
